
import { Component, inject, OnInit, TemplateRef, viewChild, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl, ValidationErrors } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { TimeRange } from './schedule-time-range-chart/schedule-time-range-chart.component';
import { SchedulerOptionsComponent } from './scheduler-options-component';
import { SchedulerService } from '../../../services/scheduler/scheduler.service';
import { BaseComponent } from '@nucleus/ui-base';
import { Event, Frequency, SchedulerDefinition, JobUpsertRequest } from '../../../api/scheduler-service';
import { TaskDefinition } from '../../../api/runner-service';
import { TaskDefinitionControllerService } from '../../../api/runner-service/api/taskDefinitionController.service';
import { CapabilityControllerService } from '../../../api/capability-service/api/capabilityController.service';
import { CapabilityRecord } from '../../../api/capability-service';
import { buildSchemaFromCapabilityFields, JsonObjectSchema } from '../../capability-schema.builder';
import { GanttViewType } from '@worktile/gantt';
import { TimeRangeSelectorComponent } from './time-range-selector/time-range-selector.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SchedulerEventsGanttComponent } from './scheduler-events-gantt/scheduler-events-gantt.component';
import { SimulationParams } from './simulation-options/simulation-options.component';


export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
    timeInput: 'HH:mm'
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
    timeInput: 'HH:mm',
    timeOptionLabel: 'HH:mm'
  },
};

@Component({
  standalone: false,
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrl: './schedule.component.css',
  providers: [ provideMomentDateAdapter(MY_FORMATS)]
})
export class ScheduleComponent extends BaseComponent implements OnInit {
  ganttComponent = viewChild<SchedulerEventsGanttComponent>("SCHEDULER_EVENTS_GANTT");

  scheduleOptionsComponent = viewChild<SchedulerOptionsComponent>("SCHEDULER_OPTIONS");
  timeRangesSelectorComponent = viewChild<TimeRangeSelectorComponent>("TIME_RANGES_SELECTOR");
  // globalError field retired — service errors route through
  // BaseComponent.showError() (ngx-toastr) per the goldfish pattern.

  fb: FormBuilder = inject(FormBuilder);
  sanitizer: DomSanitizer = inject(DomSanitizer);

  schedulerService : SchedulerService = inject(SchedulerService);
  override dialog: MatDialog = inject(MatDialog);

  /** TASK-167: route the operator landed on from the Task Catalog "Schedule
   *  this task" action carries the task identity as query params. Captured
   *  here so the schedule the operator builds is bound to the right task
   *  type + version at submit time. Either both are set (catalog handoff)
   *  or both are null (operator opened /scheduling/schedule directly and
   *  will pick a task via a future picker — TODO once the picker lands).
   *
   *  Independent of the schedulerForm because the task identity isn't a
   *  schedule-shape concern — it's metadata the schedule is FOR, carried
   *  alongside the schedulerDefinition when the wizard submits. */
  readonly route = inject(ActivatedRoute);
  readonly taskDefinitions = inject(TaskDefinitionControllerService);

  /** Job name (TASK-221 SAVE) — required to persist a schedule. */
  jobName: FormControl = new FormControl(null, Validators.required);
  saving = false;
  selectedTask: TaskDefinition | null = null;
  selectedTaskError: string | null = null;

  /** The capability descriptor rendered as a schedule-input form (TASK-221).
   *  Built from the capability's INPUT capability_field rows via the x-options
   *  bridge — Connector fields become live connection pickers, userdata fields
   *  the UserData picker, etc. null = the task declares no inputs (or none
   *  registered yet) → no form. */
  readonly capabilityController = inject(CapabilityControllerService);
  taskInputSchema: JsonObjectSchema | null = null;
  /** Captured task-input payload (values + chosen connection ids + userdata
   *  keys). Travels with the schedule at submit; the scheduler maps connector
   *  fields → ExecuteCommand.bindings at dispatch. */
  taskPayload: any = {};
  taskInputsValid = true;

  /** The capability (code, version) the SAVE pins to — set by EITHER the Task
   *  Catalog handoff (loadCapabilityInputs) or the direct-open picker. */
  selectedCapability: CapabilityRecord | null = null;
  /** ACTIVE capability catalog for the direct-open picker. */
  capabilities: CapabilityRecord[] = [];

  /** mat-select compares capabilities by (code, version), not reference, so a
   *  capability loaded via detail() still highlights its picker option. */
  compareCapabilities = (a: CapabilityRecord | null, b: CapabilityRecord | null): boolean =>
    !!a && !!b && a.code === b.code && a.version === b.version;

  @ViewChild('previewDialog') previewDialogRef!: TemplateRef<unknown>;
  viewType = GanttViewType.hour;

  frequencies = Object.values(Frequency);

  frequencyControl : FormControl = new FormControl(null);
  schedulerForm!: FormGroup;

  startDate : FormControl = new FormControl(new Date(), Validators.required);
  endDate : FormControl = new FormControl(null);

  /** IANA timezone the whole schedule resolves through. Default to the user's
   *  browser zone (Intl.DateTimeFormat().resolvedOptions().timeZone) so the
   *  initial value matches "what the operator expected to see"; UTC is the
   *  backend default if the browser doesn't expose a zone. */
  timeZone : FormControl = new FormControl(this.detectBrowserTimeZone());

  /** All IANA zones the browser knows about. Modern Chrome/Firefox/Edge
   *  expose `Intl.supportedValuesOf('timeZone')`; older browsers fall back
   *  to a curated short list. */
  availableTimeZones: string[] = this.computeAvailableTimeZones();
  self = this;
  events : Event[] = [];
  eventDependency : boolean = false;
  description : String = "";

  /** Simulation knobs from the Simulation tab — fed to scheduler-events-gantt
   *  AND scheduler-events-table so both views reflect the same user-chosen
   *  execution duration / jitter / failure rate. */
  simulation: SimulationParams = { durationMs: 60_000, jitterPercent: 0, failurePercent: 0, driftMs: 0, sampleSize: 10 };

  /** Which preview is mounted — the same events feed both. The table view is
   *  better for reading exact times / drift / jitter values; the Gantt is
   *  better for spotting overlaps and pacing. */
  viewMode: 'gantt' | 'table' = 'gantt';

  /** Preview pane visibility. Hidden by default — operators open it on demand
   *  via the page-header "Preview firings" toggle. Mirrors the embedded-in-
   *  task-def shape we expect long-term. */
  previewOpen = false;

  /** Fullscreen mode — the preview pane covers the whole viewport, hiding
   *  the form drawer underneath. Useful for "show me the whole thing" /
   *  print scenarios. */
  previewFullscreen = false;

  togglePreview(): void {
    this.previewOpen = !this.previewOpen;
    if (!this.previewOpen) this.previewFullscreen = false;
    if (this.previewOpen) {
      // Drawer width snaps instantly now (CSS transition: none), so the
      // worktile gantt sees its final viewport size on first measurement.
      // Still fire two staggered resize events as a safety net — covers
      // the case where the gantt mounts before its container's height
      // chain has fully resolved (mat-drawer-content lazy layout). The
      // refreshGantt re-fetches events for the new view.
      setTimeout(() => {
        this.refreshGantt();
        window.dispatchEvent(new Event('resize'));
      }, 0);
      setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
    }
  }

  /** Re-fire a resize event whenever viewMode flips so the freshly-mounted
   *  gantt or table gets a chance to measure its container correctly. */
  onViewModeChange(mode: 'gantt' | 'table'): void {
    this.viewMode = mode;
    setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
  }

  /** Material icon name per frequency — used by the Frequency toggle group
   *  in the Schedule section. Keep these mnemonic + consistent across
   *  TaskSense / GoldFish / BookWise if any of them grow the same toggle. */
  getFrequencyIcon(freq: string): string {
    switch (freq) {
      case 'ONCE':    return 'flag';
      case 'HOURLY':  return 'hourglass_empty';
      case 'DAILY':   return 'today';
      case 'WEEKLY':  return 'date_range';
      case 'MONTHLY': return 'calendar_month';
      case 'YEARLY':  return 'calendar_today';
      case 'CRON':    return 'code';
      default:        return 'schedule';
    }
  }

  toggleFullscreen(): void {
    this.previewFullscreen = !this.previewFullscreen;
  }

  /** Print the table view via the browser. A print-only stylesheet
   *  (in schedule.component.css) hides every page chrome element so only
   *  the events-table renders on paper. */
  printTable(): void {
    document.body.classList.add('printing-schedule-table');
    window.print();
    setTimeout(() => document.body.classList.remove('printing-schedule-table'), 0);
  }

  // Preview-options dialog replaces the previous sidebar (cleaner separation —
  // Gantt keeps full real estate, the literal strip + Configure button is the
  // entry point).

  constructor() {
    super();
  }



  /** Browser-reported IANA zone, or 'UTC' if the runtime can't tell us. */
  private detectBrowserTimeZone(): string {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  /** Modern browsers expose `Intl.supportedValuesOf('timeZone')` which returns
   *  the full IANA list (~600 zones). Older browsers fall back to a small
   *  curated list — covers the common cases without bloating the UI. */
  private computeAvailableTimeZones(): string[] {
    try {
      const fn = (Intl as any).supportedValuesOf;
      if (typeof fn === 'function') {
        const tzs: string[] = fn('timeZone');
        return tzs?.length ? tzs : this.fallbackTimeZones();
      }
    } catch {}
    return this.fallbackTimeZones();
  }

  private fallbackTimeZones(): string[] {
    return [
      'UTC', 'America/Toronto', 'America/New_York', 'America/Chicago',
      'America/Denver', 'America/Los_Angeles', 'America/Vancouver',
      'America/Mexico_City', 'America/Sao_Paulo',
      'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
      'Europe/Rome', 'Europe/Amsterdam',
      'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Kolkata',
      'Asia/Dubai',
      'Australia/Sydney', 'Pacific/Auckland',
    ];
  }

  ngOnInit(): void {
    // NUC-RBAC v2: reaching the schedule wizard needs job:access or
    // scheduler:view. The task/capability/userdata pickers below are covered by
    // the dependency closure, so no separate gate is needed for them.
    if (!this.permissionChecker.hasAccessToViewSchedulingSchedules()) {
      this.showAccessDeny();
      this.navigateTo('/scheduling/schedules', null);
      return;
    }
    this.schedulerForm = this.fb.group({
      startDate: this.startDate,
      endDate: this.endDate,
      frequency: this.frequencyControl,
      timeZone: this.timeZone,
    }, { validators: this.dateRangeValidator } );

    this.schedulerForm.valueChanges.subscribe(() => setTimeout(() => this.refreshGantt(), 0));
    this.frequencyControl.setValue(Frequency.Daily);
    this.frequencyControl.valueChanges.subscribe(() => setTimeout(() => this.onFrequencyChanged()));

    // TASK-167: Task Catalog handoff. /scheduling/tasks's "Schedule this task"
    // button (TASK-166) routes here with ?taskType=…&definitionVersion=… —
    // load the matching TaskDefinition so the page-header shows what's being
    // scheduled and the wizard can validate the input payload against the
    // task's input schema. queryParamMap.subscribe so back-button navigation
    // refreshes; queryParamMap (vs queryParams) reads case-sensitive raw values.
    this.route.queryParamMap.subscribe(params => {
      const taskType         = params.get('taskType');
      const definitionVersion = params.get('definitionVersion');
      if (taskType && definitionVersion) {
        this.loadTaskDefinition(taskType, definitionVersion);
        this.loadCapabilityInputs(taskType, definitionVersion);
      } else {
        this.selectedTask = null;
        this.selectedTaskError = null;
        this.taskInputSchema = null;
        this.selectedCapability = null;
        this.loadCapabilityList();
      }
    });
  }

  /** Fetch the TaskDefinition from tasksense-runner-service. 404 → friendly
   *  message rather than silent failure (the operator clicked into this page
   *  expecting a specific task; if the catalog row is stale they need to know).
   *  Other errors get the standard BaseComponent toast. */
  private loadTaskDefinition(taskType: string, definitionVersion: string): void {
    this.selectedTaskError = null;
    this.taskDefinitions.findByTaskTypeAndVersion(taskType, definitionVersion).subscribe({
      next: (def) => { this.selectedTask = def ?? null; },
      error: (err) => {
        this.selectedTask = null;
        if (err?.status === 404) {
          this.selectedTaskError = `Task ${taskType}/${definitionVersion} is no longer registered. Pick a different task from the catalog.`;
        } else {
          this.showError(err, 'Failed to load task definition');
        }
      },
    });
  }

  /** Fetch the capability (keyed by taskType+version) and bridge its INPUT
   *  fields into the schedule-input schema-form. detail() carries the parsed
   *  capability_field rows (connectorType / userdataType / resource / secret),
   *  which the x-options bridge maps to the form's widgets. A task with no
   *  inputs (or no registered capability) → no form. */
  private loadCapabilityInputs(code: string, version: string): void {
    this.taskInputSchema = null;
    this.taskPayload = {};
    this.taskInputsValid = true;
    this.capabilityController.detail(code, version).subscribe({
      next: (rec) => {
        this.selectedCapability = rec ?? null;
        const schema = buildSchemaFromCapabilityFields(rec?.fields ?? []);
        this.taskInputSchema = Object.keys(schema.properties).length ? schema : null;
      },
      error: () => { this.taskInputSchema = null; this.selectedCapability = null; },
    });
  }

  /** SchemaForm value-flow — the captured task-input payload. */
  onPayloadChange(value: any): void {
    this.taskPayload = value;
  }

  /** SchemaForm validity — gates submit alongside the schedule form. */
  onInputsValidity(valid: boolean): void {
    this.taskInputsValid = valid;
  }

  /** Load the ACTIVE capability catalog for the direct-open picker. */
  private loadCapabilityList(): void {
    this.capabilityController.list('ACTIVE').subscribe({
      next: (list) => { this.capabilities = (list ?? []).slice().sort((a, b) => a.code.localeCompare(b.code)); },
      error: () => { this.capabilities = []; },
    });
  }

  /** Picker selection → load the chosen capability's descriptor form. */
  onCapabilityPicked(cap: CapabilityRecord | null): void {
    this.selectedCapability = cap;
    this.taskPayload = {};
    if (cap) this.loadCapabilityInputs(cap.code, cap.version);
    else this.taskInputSchema = null;
  }


  onFrequencyChanged() {
    this.events = [];
    setTimeout(() => 
        {
          if (this.frequencyControl.value == Frequency.Once) {
            this.viewType = GanttViewType.hour;
          } else if (this.frequencyControl.value == Frequency.Hourly) {
            this.viewType = GanttViewType.hour;
          } else if (this.frequencyControl.value == Frequency.Daily) {
            this.viewType = GanttViewType.day;
          } else if (this.frequencyControl.value == Frequency.Weekly) {
            this.viewType = GanttViewType.day;
          } else if (this.frequencyControl.value == Frequency.Monthly) {
            this.viewType = GanttViewType.day;
          } else if (this.frequencyControl.value == Frequency.Yearly) {
            this.viewType = GanttViewType.day;
          } else if (this.frequencyControl.value == Frequency.Cron) {
            this.viewType = GanttViewType.day;
          } 
        }, 0);
  }


  /** Save is enabled when the schedule shape, the task-input form, and the job
   *  name are all valid, a task is selected, and no save is in flight. */
  canSave(): boolean {
    return this.permissionChecker.hasAccessToCreateSchedule()
        && !!this.selectedCapability
        && this.schedulerForm.valid
        && this.taskInputsValid
        && this.jobName.valid
        && !this.saving;
  }

  /** Persist the schedule as a Job (TASK-221 S1 SAVE). Builds an INLINE-payload
   *  TASK job pinned to the capability version; at dispatch the scheduler maps
   *  the Connector fields in the payload to ExecuteCommand.bindings (S3). */
  submitForm(): void {
    if (!this.permissionChecker.hasAccessToCreateSchedule()) { this.showAccessDeny(); return; }
    if (!this.canSave()) {
      this.jobName.markAsTouched();
      return;
    }
    const cap = this.selectedCapability!;
    const req: JobUpsertRequest = {
      name: this.jobName.value,
      targetKind: 'TASK',
      taskType: cap.code,
      capabilityVersion: cap.version,
      inputKind: 'INLINE',
      inlinePayload: JSON.stringify(this.taskPayload ?? {}),
      scheduleDefinition: this.getScheduleDefinition() as any,
      enabled: true,
    };
    this.saving = true;
    this.schedulerService.createJob(req, {
      onStart: () => {},
      onSuccess: () => {
        this.showSuccess(`Schedule "${req.name}" saved.`);
        this.router.navigate(['/scheduling/schedules']);
      },
      onError: (err: string) => this.showError(err, 'Failed to save the schedule'),
      onComplete: () => { this.saving = false; },
    });
  }


  onMonthSelectionChanged(month : string) {
  }

  isRecurringCron() : boolean {
    return this.frequencyControl.value === Frequency.Cron;
  }

  isRecurringMonthly() : boolean {
    return this.frequencyControl.value === Frequency.Monthly;
  }

  isRecurringDaily() : boolean {
    return this.frequencyControl.value === Frequency.Daily;
  }

  isRecurringWeekly() : boolean {
    return this.frequencyControl.value === Frequency.Weekly;
  }

  isRecurringHourly() : boolean {
    return this.frequencyControl.value === Frequency.Hourly;
  }

  isRecurringYearly() : boolean {
    return this.frequencyControl.value === Frequency.Yearly;
  }


  isRecurring() : boolean {
    return this.frequencyControl.value != Frequency.Once;
  }

  isStartDateError() : boolean {
    return !this.startDate.valid;
  }

  startDateErrorMessage() : string {
    if (this.startDate.hasError('dateOrderInvalid')) {
      return 'Start date must be before the end date';
    }

    if (this.startDate.hasError('required')) {
      return 'Start Date is required';
    }
    return 'Invalid Date';
  }

  isEndDateError() : boolean {
    return !this.endDate.valid;
  }

  endDateErrorMessage() : string {
    if (this.endDate.hasError('dateOrderInvalid')) {
      return "End date must be after the start date";
    }
    return 'Invalid Date';
  }



  getScheduleDefinition() : SchedulerDefinition {
    let schedulerDefinition : SchedulerDefinition = this.scheduleOptionsComponent()?.getSchedulerDefinition()
        ?? { frequency: this.frequencyControl.value, timeRanges: [] };

    schedulerDefinition.frequency = this.frequencyControl.value;
    schedulerDefinition.startDate = this.toIsoLocal(new Date(this.startDate.value));

    if (this.endDate.value != undefined && this.endDate.value != null) {
      schedulerDefinition.endDate = this.toIsoLocal(new Date(this.endDate.value));
    }

    schedulerDefinition.timeRanges = this.getTimeRanges();
    // Timezone is parent-level — applies to every frequency. Backend default
    // is UTC; the form picked the user's browser zone on init.
    // Cast: the generated SchedulerDefinition type comes from the deployed
    // service's OpenAPI spec, which doesn't yet have `timeZone`. The field is
    // serialised through Jackson on the wire either way; this cast goes away
    // when we regen the client against the redeployed service.
    (schedulerDefinition as any).timeZone = this.timeZone.value || 'UTC';

    console.log(JSON.stringify(schedulerDefinition));

    return schedulerDefinition;
  }

  /** Backend reads startDate/endDate as LocalDateTime — naive (no zone). The
   *  shift by getTimezoneOffset preserves the wall-clock time the user picked
   *  when JSON.stringify produces a UTC ISO string. */
  private toIsoLocal(date: Date): string {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
  }

  dateRangeValidator(form: FormGroup): ValidationErrors | null {
    const start = form.get('startDate')?.value;
    const end = form.get('endDate')?.value;
    
    if (start && end && new Date(start) > new Date(end)) {
      form.get('startDate')?.setErrors({ dateOrderInvalid: true });
      form.get('endDate')?.setErrors({ dateOrderInvalid: true });

      return { dateOrderInvalid: true };
    } else {
      form.get('startDate')?.setErrors(null);
      form.get('endDate')?.setErrors(null);
    }

    form.get('startDate')?.markAsTouched();
    form.get('endDate')?.markAsTouched();
    return null;
  }

  getTimeRanges() : TimeRange[] {
    return this.timeRangesSelectorComponent()?.getTimeRanges() || [];
  }


  refreshGantt() {
    let sd : SchedulerDefinition = this.getScheduleDefinition();
    this.eventDependency = !!sd.eventDependency;

    setTimeout(() => {
      // Sample size comes from the Simulation tab. Was hardcoded 10; now the
      // operator can dial up to 500 for long-range preview on dense hourly
      // patterns ("show me 200 fires of every-13-min weekday-only").
      const sampleSize = this.simulation?.sampleSize ?? 10;
      this.schedulerService.calculate(sd, sampleSize, {
        onStart: () => { },
        onSuccess: (r) => {
          setTimeout(() => {
              setTimeout(() => this.getSchedulerDefinitionDescription(sd), 0);
              if (r.length > 0) {
                this.events = r;
              }
            }, 0);
        },
        // Goldfish-aligned global-error pattern: route service errors through
        // BaseComponent.showError() which renders via ngx-toastr (top-right).
        // Was setting `this.globalError = error` and rendering an inline
        // `<mat-error>` banner bottom-of-page; both retired in favour of the
        // unified toast pattern. The `error` string here is already the
        // friendly mapping from RequestHandler.friendlyMessage().
        onError: (error: string) => {
          this.description = "";
          this.showError(error);
        },
        onComplete: () => { }
      }
      );
    } , 0);

  }

  getSchedulerDefinitionDescription(sd : SchedulerDefinition) {

    this.schedulerService.describe(sd, {
      onStart: () => { },
      onSuccess: (r) => {
        this.description = r;
      },
      onError: (error: string) => this.showError(error),
      onComplete: () => { }
    }
    );
  }



  sanitizeHtml(html : string) : SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  downloadGantt() {
    // Pass the schedule literal as the filename hint — turns "Daily at 09:30
    // (timezone: America/Toronto)" into a friendly slug like "daily-at-09-30…".
    this.ganttComponent()?.downloadGanttImage(this.description?.toString());
  }

  /** Open the preview-options dialog (the simulation form lives there now,
   *  reachable from the literal-strip "Configure preview" button). The dialog
   *  binds (paramsChange) the same way the inline tab used to, so live edits
   *  propagate to refreshGantt() while the dialog is open.
   *
   *  We store the dialogRef so the X button in the template can close it
   *  via (click)="closePreviewDialog()". `mat-dialog-close` doesn't fire
   *  reliably from inside a TemplateRef-based dialog (the directive resolves
   *  against the parent component's injector, not the dialog container). */
  private openPreviewDialogRef: MatDialogRef<unknown> | null = null;

  openPreviewDialog(): void {
    this.openPreviewDialogRef = this.dialog.open(this.previewDialogRef, {
      panelClass: 'bw-dialog-panel',
      width: '560px',
      maxWidth: '92vw',
      autoFocus: 'first-tabbable',
    });
    this.openPreviewDialogRef.afterClosed().subscribe(() => {
      this.openPreviewDialogRef = null;
    });
  }

  closePreviewDialog(): void {
    this.openPreviewDialogRef?.close();
  }

  /** Apply button — asks the simulation-options inside the dialog to commit
   *  its current form state (one-shot paramsChange emit), then closes the
   *  dialog. Cancel just closes; the dialog's internal form state is
   *  discarded with the component instance. */
  applyPreviewDialog(opts: { commit: () => void } | null | undefined): void {
    opts?.commit();
    this.closePreviewDialog();
  }

  /** Has the operator dialed ANY non-default simulation knob? Drives whether
   *  the "SIM" chip surfaces in the page-header stats strip. Sample size is
   *  intentionally ignored here — it doesn't change the simulated *behavior*,
   *  only how many fires we preview, so it doesn't deserve a chip. */
  isSimulationActive(): boolean {
    const s = this.simulation;
    if (!s) return false;
    return (s.durationMs > 0)
        || (s.jitterPercent > 0)
        || (s.failurePercent > 0)
        || (s.driftMs !== 0);
  }

  /** Plain-English summary of the current preview options — drives the literal
   *  strip above the Gantt, mirroring the schedule-literal banner pattern. */
  previewLiteral(): string {
    const sim = this.simulation;
    if (!sim) return 'Sample 10 events';
    const parts: string[] = [];
    parts.push(`Sample ${sim.sampleSize ?? 10} event${(sim.sampleSize ?? 10) === 1 ? '' : 's'}`);
    if (sim.durationMs > 0) parts.push(`bars ${this.formatDuration(sim.durationMs)}`);
    if (sim.jitterPercent > 0) parts.push(`±${sim.jitterPercent}% jitter`);
    if (sim.failurePercent > 0) parts.push(`${sim.failurePercent}% fail`);
    if (sim.driftMs !== 0) {
      const sign = sim.driftMs > 0 ? '+' : '−';
      parts.push(`${sign}${this.formatDuration(Math.abs(sim.driftMs))} drift`);
    }
    return parts.join(' · ');
  }

  private formatDuration(ms: number): string {
    if (ms >= 3_600_000 && ms % 3_600_000 === 0) return `${ms / 3_600_000}h`;
    if (ms >= 60_000 && ms % 60_000 === 0) return `${ms / 60_000}m`;
    if (ms >= 1_000 && ms % 1_000 === 0) return `${ms / 1_000}s`;
    return `${ms}ms`;
  }

  /** Simulation tab → gantt. Three classes of change:
   *  (a) sampleSize moved → need to refetch from backend (different N events).
   *  (b) duration / jitter / failure / drift moved → no refetch; gantt
   *      re-decorates via @Input change detection on ngOnChanges.
   *  (c) the master enabled toggle flipped → fall through to (b).
   *
   *  Refresh aggressively for ANY change (not just sampleSize) so the user
   *  sees instant feedback as they drag sliders. The backend call is cheap
   *  (no DB write, just calculator iteration), so the convenience trumps
   *  the few extra HTTP calls. If this becomes noisy in practice we can
   *  debounce with a 200ms timer here, but ship reactive-first. */
  onSimulationChanged(params: SimulationParams): void {
    this.simulation = params;
    setTimeout(() => this.refreshGantt(), 0);
  }

  /** Total simulated wall-clock the gantt is currently painting, summed across
   *  every execution. Drives the Stats strip in the page header. */
  totalSimulatedMs(): number {
    if (!this.simulation.durationMs) return 0;
    let count = 0;
    for (const e of this.events) {
      count += (e.executions ?? []).length;
    }
    return count * this.simulation.durationMs;
  }

  /** Friendly version of totalSimulatedMs() for the page-header stat. */
  totalSimulatedLabel(): string {
    const ms = this.totalSimulatedMs();
    if (!ms) return '—';
    if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)}m`;
    if (ms < 86_400_000) return `${(ms / 3_600_000).toFixed(1)}h`;
    return `${(ms / 86_400_000).toFixed(1)}d`;
  }

  /** Sum of executions across all events — drives the "Events" stat. */
  executionCount(): number {
    let count = 0;
    for (const e of this.events) {
      count += (e.executions ?? []).length;
    }
    return count;
  }

}